import React from 'react';
import { IconCheck, IconX } from './Icons';
import { TIER_CAPABILITY_GROUPS, TIER_COLUMNS } from '../utils/tierComparisonMatrix';
import './TierComparisonMatrix.css';

function CellValue({ value }) {
  if (value === true) {
    return (
      <span className="tier-matrix-cell tier-matrix-cell--yes" aria-label="Included">
        <IconCheck size={16} />
      </span>
    );
  }
  if (value === 'partial') {
    return (
      <span className="tier-matrix-cell tier-matrix-cell--partial" title="Limited / dev override only">
        ~
      </span>
    );
  }
  return (
    <span className="tier-matrix-cell tier-matrix-cell--no" aria-label="Not included">
      <IconX size={16} />
    </span>
  );
}

/**
 * Columns a viewer may see: their own tier and everything below it. Nobody
 * sees what the tiers above them can do (Users page rule).
 * @param {'admin'|'vadminPlus'|'vadmin'} viewerTier
 */
export function visibleTierColumns(viewerTier) {
  const idx = TIER_COLUMNS.findIndex((col) => col.id === viewerTier);
  return idx === -1 ? TIER_COLUMNS.filter((col) => col.id === 'basic') : TIER_COLUMNS.slice(0, idx + 1);
}

/** Groups and rows that mean something to the visible columns (drops rows only a hidden tier has). */
export function visibleCapabilityGroups(columns) {
  return TIER_CAPABILITY_GROUPS
    .map((group) => ({
      ...group,
      rows: group.rows.filter((row) => columns.some((col) => row[col.id] === true || row[col.id] === 'partial')),
    }))
    .filter((group) => group.rows.length > 0);
}

export default function TierComparisonMatrix({ viewerTier = 'admin' }) {
  const columns = visibleTierColumns(viewerTier);
  const groups = visibleCapabilityGroups(columns);
  const isPlatformAdmin = viewerTier === 'admin';
  return (
    <section className="tier-matrix-section" aria-labelledby="tier-matrix-heading">
      <header className="tier-matrix-header">
        <h2 id="tier-matrix-heading">Tier capability comparison</h2>
        {isPlatformAdmin ? (
          <p>
            What each effective tier can do in the app. Allowlists on this page grant
            Platform Admin, Brand Admin, Dev Max, Dev Pro, or Dev Basic. Pro and Max are also
            available via Whop (and Dev Max list until Whop Max ships).
          </p>
        ) : (
          <p>What each tier, up to yours, can do in the app.</p>
        )}
      </header>

      <div className="tier-matrix-legend">
        <span className="tier-matrix-legend-item tier-matrix-cell--yes">
          <IconCheck size={14} /> Included
        </span>
        <span className="tier-matrix-legend-item tier-matrix-cell--no">
          <IconX size={14} /> Not included
        </span>
        <span className="tier-matrix-legend-item tier-matrix-cell--partial">~ Limited / dev override</span>
      </div>

      <div className="tier-matrix-scroll">
        <table className="tier-matrix-table">
          <thead>
            <tr>
              <th scope="col" className="tier-matrix-feature-col">Capability</th>
              {columns.map((col) => (
                <th key={col.id} scope="col" className="tier-matrix-tier-col">
                  <span className="tier-matrix-tier-label">{col.label}</span>
                  <span className="tier-matrix-tier-grant">{col.grant}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <React.Fragment key={group.title}>
                <tr className="tier-matrix-group-row">
                  <th colSpan={columns.length + 1} scope="colgroup">{group.title}</th>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.label}>
                    <th scope="row" className="tier-matrix-feature-col">
                      {row.label}
                      {row.note ? (
                        <span className="tier-matrix-row-note">{row.note}</span>
                      ) : null}
                    </th>
                    {columns.map((col) => (
                      <td key={col.id}>
                        <CellValue value={row[col.id]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
